/**
 * @file mixin to handle drag/drop inside grid
 */
import { captureException, captureMessage } from 'bits/error_tracker'
import { $ } from 'bits/jquery'
import shapeshift from 'bits/shapeshift'
import { mapState as piniaMapState } from 'pinia'
import { useSurfacePermissionsStore } from 'pinia/surface_permissions'
import { computeAndSaveNewPostIndex } from 'surface/sorter' // TODO: Remove
import { mapActions } from 'vuex'

/** Usage:
 *  On mount, call initializeGrid
 */

export default {
  data() {
    return {
      $_gridMixin_grid: false,
      $_gridMixin_initialized: false,
      $_gridMixin_isDragging: false,
      $_gridMixin_isShapeshifting: false,
      $_gridMixin_observer: null,
      $_gridMixin_savePostHandler: null,
      $_gridMixin_options: {},
    }
  },
  computed: {
    ...piniaMapState(useSurfacePermissionsStore, ['canIDragPosts']),
  },
  created() {
    shapeshift()
    this.$_gridMixin_lazyUpdateGrid = this.debounce(this.$_gridMixin_updateGrid, 125)
  },
  updated() {
    this.debouncedUpdateGrid()
  },
  beforeDestroy() {
    this.$_gridMixin_destroyGrid()
    this.$_gridMixin_savePostHandler = null
    this.$_gridMixin_observer && this.$_gridMixin_observer.disconnect()
  },
  methods: {
    ...mapActions(['changeDragging']),
    initializeGrid(node, savePostHandler, options = {}) {
      this.$_gridMixin_initialized = true
      this.$_gridMixin_savePostHandler = savePostHandler
      if (!savePostHandler) {
        captureMessage('Using GridMixin without declaring a handler function to save post.')
      }
      this.$_gridMixin_options = options
      this.$_gridMixin_observe(node)
    },
    debounce(func, wait, immediate) {
      let timeout
      return () => {
        const args = arguments
        const later = () => {
          timeout = null
          if (!immediate) func.apply(this, args)
        }
        const callNow = immediate && !timeout
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
        if (callNow) func.apply(this, args)
      }
    },
    $_gridMixin_savePost({ postCid, previousCid, nextCid }) {
      if (this.$_gridMixin_savePostHandler) {
        this.$_gridMixin_savePostHandler({ postCid, previousCid, nextCid })
      } else {
        captureException(new Error('No handler to save post for GridMixin.'))
      }
    },
    debouncedUpdateGrid() {
      if (this.$_gridMixin_initialized) {
        if (this.$_gridMixin_grid) {
          this.$_gridMixin_lazyUpdateGrid()
        } else {
          this.$_gridMixin_makeGrid()
          this.$_gridMixin_registerDomChangeListenersForGrid()
        }
      } else {
        this.$_gridMixin_destroyGrid()
      }
    },
    $_gridMixin_onGridFinishDragging() {
      if (this.onGridFinishDragging) {
        this.onGridFinishDragging()
      }
    },
    $_gridMixin_makeGrid() {
      this.$_gridMixin_grid = true
      const $root = $(this.$el)
      $root.addClass('wish-not-being-dragged') // Some animation should only be applied when wishes are idle
      this.$_gridMixin_shapeshift()
      $root.on('ss-rearranged', (e, item) => {
        computeAndSaveNewPostIndex(item)
      })
      $root.on('ss-arranged', (e) => {
        $root.find('.surface-post').each((index, el) => {
          $(el).attr('data-index', index + 1)
          $(el)
            .find('.wish-content')
            .attr('data-index', index + 1)
        })
      })
      $root.on('ss-drag-start', (e) => {
        $root.removeClass('wish-not-being-dragged') // Some animation should only be applied when wishes are idle
        this.isDragging = true
        this.changeDragging({ isAPostDragging: true })
      })
      $root.on('ss-drop-complete', (e) => {
        $root.addClass('wish-not-being-dragged') // Some animation should only be applied when wishes are idle
        this.$_gridMixin_onGridFinishDragging()
        this.isDragging = false
        this.changeDragging({ isAPostDragging: false })
      })
    },
    $_gridMixin_destroyGrid() {
      $(this.$el).off('.grid').trigger('ss-destroy')
      this.$_gridMixin_grid = false
      $(this.$el).off('.grid', 'textarea,img')
    },
    $_gridMixin_updateGrid() {
      this.$_gridMixin_shapeshift()
    },
    $_gridMixin_observe(node) {
      const config = { attributes: true, childList: true, subtree: true }
      const callback = () => {
        if (this.isDragging || this.isShapeshifting) return
        this.debouncedUpdateGrid()
      }
      const observer = new MutationObserver(callback)
      observer.observe(node, config)
      this.$_gridMixin_observer = observer
    },
    $_gridMixin_shapeshift() {
      this.isShapeshifting = true
      const $root = $('#wish-list')
      $root.trigger('ss-destroy')
      if ($root.find('.surface-post').length !== 0) {
        const containerPaddingX = parseInt($root.css('paddingLeft'))
        const containerPaddingY = parseInt($root.css('paddingTop'))
        const gutterWidth = containerPaddingX
        $root.shapeshift({
          selector: '.surface-post:not(.wish-leave)', // wishes that is leaving are excluded in grid laying-out
          enableDrag: this.canIDragPosts,
          enableCrossDrop: false,
          enableResize: false,
          enableTrash: false,
          dragWhitelist: '.wish-editable:not(#wish-new)',
          gutterX: gutterWidth,
          gutterY: gutterWidth,
          paddingY: containerPaddingY,
          paddingX: containerPaddingX,
          ...this.$_gridMixin_options,
        })
      }
      // Flush detected mutations to observer
      setTimeout(() => (this.isShapeshifting = false), 0)
    },
    $_gridMixin_registerDomChangeListenersForGrid() {
      $(this.$el).on('input.grid', 'textarea', () => {
        this.$_gridMixin_lazyUpdateGrid()
      })
      $(this.$el).on('beethoven-load.grid', 'img', () => {
        this.$_gridMixin_lazyUpdateGrid()
      })
    },
  },
  watch: {
    canIDragPosts(canDrag) {
      const $root = $('#wish-list')
      $root.find('.surface-post.ui-draggable-handle').each((_, el) => {
        $(el).draggable(canDrag ? 'enable' : 'disable')
      })
    },
  },
}
